import type { Cruz, Vehicle } from '@cruz-com/lookup'
import { deleteCookie, getCookies, setCookie } from 'cookies-next/client'
import dayjs from 'dayjs'
import { jwtDecode } from 'jwt-decode'
import _ from 'lodash'
import { createStore } from 'zustand/vanilla'
import { myCruzApi } from '@/api'
import type { Toast } from '@/types'

type SpottedFilter = {
  page_size?: number
  sort_id?: number
}

type VehicleFilter = {
  page_size?: number
  sort_id?: number
  zip_code?: string
  radius?: number
}

type ModalSignIn = {
  show: boolean
  saveVehicleId?: string | null
  like?: {
    name: string
    id: string
  }
  saveSearch?: boolean
}

type Lead = {
  type: number
  vehicle_id: string
  vehicle_title: string
}

export type GlobalState = {
  user: Cruz.User | null
  toast?: Toast | null
  refreshMyProfile?: boolean
  spottedFilter?: SpottedFilter
  vehicleFilter?: VehicleFilter
  showSignIn?: boolean
  modalSignIn?: ModalSignIn
  lead: Lead
  messageToOwnerType: number
  scrollTopOver: boolean
  showFeedback: boolean
  compareVehicleIds: string[]
  aiSearchVehicles?: Vehicle.Summary[]
}

export type GlobalActions = {
  setUser: (user: Cruz.User | null, pathname: string | null) => void
  setToast: (toast: Toast | null) => void
  setRefreshMyProfile: (refreshMyProfile: boolean) => void
  setSpottedFilter: (spottedFilter: SpottedFilter) => void
  setVehicleFilter: (vehicleFilter: VehicleFilter) => void
  setModalSignIn: (modalSignIn: ModalSignIn) => void
  setLead: (lead: Lead) => void
  setMessageToOwnerType: (messageToOwnerType: number) => void
  setScrollTopOver: (scrollTopOver: boolean) => void
  setShowFeedback: (showFeedback: boolean) => void
  setCompareVehicleIds: (compareVehicleIds: string[]) => void
  setAiSearchVehicles: (aiSearchVehicles: Vehicle.Summary[]) => void
  refreshMyStatistic: () => void
}

export type GlobalStore = GlobalState & GlobalActions

async function transferRecentlyViewed(): Promise<number> {
  try {
    const rvIds: { id: string; date: string }[] = []
    if (typeof document !== 'undefined') {
      const cookies = getCookies()
      _.forEach(cookies, (value, name) => {
        if (!name.startsWith('vehicle-')) {
          return
        }
        const id = name.replace('vehicle-', '')
        const date = decodeURIComponent(value as string)
        if (dayjs(date).isValid()) {
          rvIds.push({ id, date })
        } else {
          deleteCookie(name)
        }
      })
    }
    if (rvIds.length > 0) {
      const d = await myCruzApi.transferRecentlyViewed(rvIds)
      return Promise.resolve(d)
    }
    return Promise.resolve(0)
  } catch {
    return Promise.resolve(0)
  }
}

export const createGlobalStore = (
  initState: GlobalState = {
    user: null,
    toast: null,
    lead: { type: 0, vehicle_id: '', vehicle_title: '' },
    messageToOwnerType: 0,
    scrollTopOver: false,
    showFeedback: false,
    compareVehicleIds: [],
  },
) => {
  return createStore<GlobalStore>()((set, get) => ({
    ...initState,
    setUser: async (user) => {
      if (user?.refresh_token) {
        setCookie('refresh_token', user.refresh_token, {
          expires: dayjs().add(1, 'year').toDate(),
        })
      }
      if (user?.access_token) {
        const decoded = jwtDecode(user.access_token)
        if (decoded.exp) {
          setCookie('access_token', user.access_token, {
            expires: new Date(decoded.exp * 1000),
          })
        }
      }
      set({ user: _.omit(user, ['refresh_token', 'access_token']) })
      if (_.isEmpty(initState?.user) && !_.isEmpty(user)) {
        // store recently viewed to database when user first time login
        // and refresh statistic
        const t = await transferRecentlyViewed()
        if (t > 0) {
          const d = await myCruzApi.statistic()
          set(() => ({
            user: { ...user, statistic: d },
          }))
        }
      }
    },
    refreshMyStatistic: async () => {
      if (!_.isEmpty(get().user)) {
        const d = await myCruzApi.statistic()
        set((state) => ({
          user: state.user ? { ...state.user, statistic: d } : null,
        }))
      }
    },
    setToast: (toast) => {
      set({ toast })
    },
    setRefreshMyProfile: (refreshMyProfile) => {
      set({ refreshMyProfile })
    },
    setSpottedFilter: (spottedFilter) => {
      const d = _.pick(spottedFilter, ['page_size', 'sort_id'])
      set({ spottedFilter: d })
      setCookie('spotted_filter', JSON.stringify(d), {
        expires: dayjs().add(1, 'year').toDate(),
      })
    },
    setVehicleFilter: (vehicleFilter) => {
      const d = _.pick(vehicleFilter, ['page_size', 'sort_id', 'zip_code', 'radius'])
      set({ vehicleFilter: d })
      setCookie('vehicle_filter', JSON.stringify(d), {
        expires: dayjs().add(1, 'year').toDate(),
      })
    },
    setModalSignIn: (modalSignIn) => {
      set({ modalSignIn })
    },
    setLead: (lead) => {
      set({ lead })
    },
    setMessageToOwnerType(messageToOwnerType) {
      set({ messageToOwnerType })
    },
    setScrollTopOver: (scrollTopOver) => {
      set({ scrollTopOver })
    },
    setShowFeedback: (showFeedback) => {
      set({ showFeedback })
    },
    setCompareVehicleIds: (compareVehicleIds) => {
      set({ compareVehicleIds })
    },
    setAiSearchVehicles: (aiSearchVehicles) => {
      set({ aiSearchVehicles })
    },
  }))
}
